import { Alert, Spin } from 'antd';
import React, { useEffect } from 'react';

import { useMasterSiteId, useSelector } from '@src/utils/hooks';
import { useQuery, useQueryClient } from 'react-query';
import { apiUtils, authFetch } from '@src/utils';
import { GetDashboardResponse, ListEnabledDashboardsResponse } from '@src/api/dashboard';
import { NewForgeExperienceAlert } from '@src/components/new-forge-experience-alert';
import { getHasAccessTo } from '@src/modules/auth/auth-selectors';

import { FormattedMessage } from '@src/i18n';
import { stylesheet } from 'typestyle';

const styles = stylesheet({
  container: { display: 'flex', flexDirection: 'column', gap: 24, flexGrow: 1, minHeight: 0 },
  iframe: { border: 0, flexGrow: 1 },
});

// the ordered list of supported dashboards in MAD
const dashboardsOrder = ['CHECKOUT', 'TRANSPORT_ADMINISTRATION', 'TRACKING'] as const;

export type DashboardType = typeof dashboardsOrder[number];

// the new app puts all dashboards on one page, one tab per type
const forgeDashboardTab: Record<DashboardType, string> = {
  CHECKOUT: 'checkout',
  TRANSPORT_ADMINISTRATION: 'transport',
  TRACKING: 'tracking',
};

type DashboardProps = {
  type: DashboardType;
  fallback: React.ReactNode;
};

export const Dashboard = ({ type, fallback }: DashboardProps) => {
  const { data, isLoading, error, isStale, refetch } = useDashboardData(type);

  useEffect(() => {
    if (isStale) {
      refetch();
    }
  }, [isStale]);

  if (isLoading) {
    return <Spin />;
  }

  if (!data?.enabled || error) {
    return <>{fallback}</>;
  }

  const displayLimitedAccessInfo = data.variant === 'LIGHT';

  return (
    <>
      <NewForgeExperienceAlert path={`/dashboard?type=${forgeDashboardTab[type]}`} />
      <div className={styles.container}>
        {displayLimitedAccessInfo && (
          <Alert
            type="info"
            message={
              <FormattedMessage
                id="DASHBOARD_LIMITED_ANALYTICS_ACCESS_INFO"
                values={{
                  mailto: ([email]: string[]) => <a href={`mailto:${email}`}>{email}</a>,
                }}
              />
            }
            showIcon
          />
        )}
        <iframe className={styles.iframe} id="dashboard" title="dashboard" src={data?.iframe_url} />
      </div>
    </>
  );
};

export const useDashboardData = (type: DashboardType) => {
  const mainSiteId = useMasterSiteId();
  const queryKey = ['dashboard', { mainSiteId, type }];
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKey,
    onSuccess: response => {
      const staleTime =
        response.enabled && response?.expires_at
          ? new Date(response.expires_at).getTime() - new Date().getTime()
          : Infinity;
      queryClient.setQueryDefaults(queryKey, {
        staleTime,
      });
    },
    enabled: !!(mainSiteId && type),
    retry: 1,
    queryFn: () =>
      authFetch<GetDashboardResponse>(
        apiUtils.getApiUrl() +
          `/frontend/dashboard/dashboard/${mainSiteId}/get?dashboard_type=${type}`
      ),
  });
};

export const useEnabledDashboards = (): DashboardType[] => {
  const siteId = useMasterSiteId();
  const queryKey = ['enabled_dashboards', { siteId }];

  const hasAccessTo = useSelector(getHasAccessTo);

  // If someone wants to use the dashboard on a site, they need the right permissions.
  // There is already a backend validation on the BE so you can't generate dashboard url without mad:dashboard.
  const hasAccessToDashboard = hasAccessTo('mad:dashboard');

  const { data: dashboards } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      const { dashboards = [] } = await authFetch<ListEnabledDashboardsResponse>(
        apiUtils.getApiUrl() + `/frontend/dashboard/enabled_dashboards/${siteId}`
      );
      // the item in the enabled dashboards list is not a dashboard type enum, but the lower case of the config key
      // converting it to the DashboardType-like format.
      return dashboards.map(e => e.toUpperCase());
    },
    staleTime: Infinity,
    enabled: hasAccessToDashboard && !!siteId,

    // rerender dependant components only when the `data` changes
    // hook depends only on the `data` value, so there is no point to rerender for the other fields
    // NOTE: test carefully when changing as it can cause infinite render loop in case of request error.
    notifyOnChangeProps: ['data'],
  });

  if (!hasAccessToDashboard || !dashboards?.length) {
    return [];
  }

  const canAccessDashboardFlags: Partial<Record<DashboardType, boolean>> = {
    TRACKING: hasAccessTo('tracking-feature:view') && hasAccessTo('mad:tracking_numbers'),
  };

  return dashboardsOrder.filter(
    type => dashboards.includes(type) && (canAccessDashboardFlags[type] ?? true)
  );
};
